import React, { useState, useEffect } from 'react';
import { FaWallet, FaSpinner, FaBrain, FaExclamationTriangle, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  assets: '#34D399',
  liabilities: '#F87171',
  netWorth: '#60A5FA',
  liquid: '#FFD700',
  nonLiquid: '#A78BFA'
};

const NetWorthSnapshot = ({ clientId }) => {
  const [loading, setLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [nonLiquidAssets, setNonLiquidAssets] = useState(0);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [clientId]);

  // Auto-fetch AI analysis when data is loaded
  useEffect(() => {
    if (!loading && (investments.length > 0 || nonLiquidAssets > 0 || liabilities.length > 0)) {
      fetchAIAnalysis();
    }
  }, [loading, investments, nonLiquidAssets, liabilities]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, liabRes, netWorthRes] = await Promise.all([
        axios.get(`${API}/investments/${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/liabilities/${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/net-worth/${clientId}`).catch(() => ({ data: null }))
      ]);
      
      setInvestments(invRes.data || []);
      setLiabilities(liabRes.data || []);
      
      if (netWorthRes.data) {
        setNonLiquidAssets(netWorthRes.data.non_liquid_assets || 0);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const saveNetWorth = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/net-worth`, {
        client_id: clientId,
        liquid_assets: liquidAssets,
        non_liquid_assets: nonLiquidAssets,
        total_liabilities: totalLiabilities
      });
    } catch (error) {
    } finally {
      setSaving(false);
    }
  };

  const fetchAIAnalysis = async () => {
    setLoadingAI(true);
    try {
      const response = await axios.post(`${API}/net-worth/ai-analysis`, {
        client_id: clientId,
        liquid_assets: liquidAssets,
        non_liquid_assets: nonLiquidAssets,
        total_assets: totalAssets,
        total_liabilities: totalLiabilities,
        net_worth: netWorth,
        liquidity_ratio: liquidityRatio,
        debt_ratio: debtRatio
      });
      setAiAnalysis(response.data);
    } catch (error) {
      // Generate local analysis if API fails
      setAiAnalysis(generateLocalAnalysis());
    } finally {
      setLoadingAI(false);
    }
  };

  const generateLocalAnalysis = () => {
    const insights = [];
    const actionPoints = [];

    // Liquidity Analysis
    if (liquidityRatio < 20) {
      insights.push({
        type: 'warning',
        title: 'Low Liquidity',
        message: 'Liquidity is low. May face trouble in a sudden emergency.'
      });
      actionPoints.push('Build emergency fund (Liquid Assets)');
      actionPoints.push('Review illiquid assets (Real Estate/Gold)');
    } else if (liquidityRatio >= 20 && liquidityRatio < 40) {
      insights.push({
        type: 'info',
        title: 'Moderate Liquidity',
        message: 'Liquidity is adequate but could be improved for better financial flexibility.'
      });
      actionPoints.push('Consider increasing liquid investments');
    } else {
      insights.push({
        type: 'success',
        title: 'Good Liquidity',
        message: 'You have healthy liquid assets for emergencies and opportunities.'
      });
    }

    // Debt Analysis
    if (debtRatio > 50) {
      insights.push({
        type: 'warning',
        title: 'High Debt Level',
        message: 'Debt ratio is above 50%. Focus on debt reduction.'
      });
      actionPoints.push('Prioritize high-interest debt repayment');
      actionPoints.push('Avoid taking new loans');
    } else if (debtRatio > 30) {
      insights.push({
        type: 'info',
        title: 'Moderate Debt',
        message: 'Debt levels are manageable but monitor closely.'
      });
      actionPoints.push('Create a debt repayment plan');
    } else if (debtRatio > 0) {
      insights.push({
        type: 'success',
        title: 'Healthy Debt Ratio',
        message: 'Debt is well under control. Good financial discipline!'
      });
    }

    // Net Worth Analysis
    if (netWorth > 0) {
      insights.push({
        type: 'success',
        title: 'Positive Net Worth',
        message: `Your net worth is positive. Continue building wealth!`
      });
    } else {
      insights.push({
        type: 'warning',
        title: 'Negative Net Worth',
        message: 'Liabilities exceed assets. Focus on debt reduction and savings.'
      });
      actionPoints.push('Create strict budget to increase savings');
      actionPoints.push('Consider debt consolidation');
    }

    return {
      insights,
      action_points: actionPoints,
      summary: netWorth > 0 
        ? `With a net worth of ₹${formatCurrency(netWorth)}, you're on a good financial path. Focus on maintaining liquidity while growing your investments.`
        : 'Your current financial position needs attention. Focus on reducing debt and building savings.'
    };
  };

  // Calculate values from investments - use current_value field
  const liquidAssets = investments.reduce((sum, inv) => sum + (inv.current_value || 0), 0);
  
  // Calculate total liabilities - use principal_outstanding field
  const totalLiabilities = liabilities.reduce((sum, liab) => sum + (liab.principal_outstanding || 0), 0);
  
  // Total assets and net worth
  const totalAssets = liquidAssets + nonLiquidAssets;
  const netWorth = totalAssets - totalLiabilities;
  
  // Calculate ratios
  const liquidityRatio = totalAssets > 0 ? (liquidAssets / totalAssets) * 100 : 0;
  const debtRatio = totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0;

  const formatCurrency = (value) => {
    if (!value) return '0';
    const lakhs = Math.abs(value) / 100000;
    if (lakhs >= 100) return `${value < 0 ? '-' : ''}${(lakhs / 100).toFixed(2)}Cr`;
    return `${value < 0 ? '-' : ''}${lakhs.toFixed(2)}L`;
  };

  const formatRupees = (value) => {
    if (!value) return '₹0';
    return `₹${Math.round(value).toLocaleString('en-IN')}`;
  };

  // Chart data
  const barChartData = [
    { name: 'Assets', value: totalAssets, fill: COLORS.assets },
    { name: 'Liabilities', value: totalLiabilities, fill: COLORS.liabilities },
    { name: 'Net Worth', value: Math.max(0, netWorth), fill: COLORS.netWorth }
  ];

  const assetBreakdownData = [
    { name: 'Liquid Assets', value: liquidAssets, color: COLORS.liquid },
    { name: 'Non-Liquid Assets', value: nonLiquidAssets, color: COLORS.nonLiquid }
  ].filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="networth-loading">
        <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="networth-snapshot">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h2 className="text-2xl font-bold text-[#FFD700] flex items-center space-x-3">
          <FaWallet />
          <span>Net Worth Snapshot</span>
        </h2>
        <p className="text-gray-400 mt-1">Assets vs Liabilities</p>
      </div>

      {/* Main Grid - Assets & Liabilities + Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Assets & Liabilities Inputs */}
        <div className="space-y-6">
          {/* Assets Section */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <h3 className="text-xl font-bold text-[#34D399] mb-4 border-b border-[#34D399] pb-2">Assets</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Liquid Assets</label>
                <div className="bg-[#0A1929] rounded-lg p-3 border border-[#2D5A8F]">
                  <p className="text-2xl font-bold text-white">{Math.round(liquidAssets).toLocaleString('en-IN')}</p>
                  <p className="text-gray-500 text-xs mt-1">Auto-calculated from Investments</p>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Non-Liquid Assets</label>
                <input
                  type="number"
                  value={nonLiquidAssets}
                  onChange={(e) => setNonLiquidAssets(parseFloat(e.target.value) || 0)}
                  onBlur={saveNetWorth}
                  className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white text-lg focus:border-[#FFD700] focus:outline-none"
                  placeholder="Enter non-liquid assets value"
                  data-testid="non-liquid-assets-input"
                />
                <p className="text-gray-500 text-xs mt-1">Real Estate, Gold, etc.</p>
              </div>
            </div>
          </div>

          {/* Liabilities Section */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <h3 className="text-xl font-bold text-[#F87171] mb-4 border-b border-[#F87171] pb-2">Liabilities</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Total Loans</label>
                <div className="bg-[#0A1929] rounded-lg p-3 border border-[#2D5A8F]">
                  <p className="text-2xl font-bold text-white">{Math.round(totalLiabilities).toLocaleString('en-IN')}</p>
                  <p className="text-gray-500 text-xs mt-1">Auto-calculated from Liabilities</p>
                </div>
              </div>
            </div>
          </div>

          {/* Ratios & Analysis Section */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <h3 className="text-xl font-bold text-[#FFD700] mb-4">Ratios & Analysis</h3>
            
            <div className="flex space-x-8 mb-4">
              <div>
                <span className="text-gray-400 text-sm">Liquidity Ratio: </span>
                <span className="text-xl font-bold text-white">{liquidityRatio.toFixed(0)}%</span>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Debt Ratio: </span>
                <span className="text-xl font-bold text-white">{debtRatio.toFixed(0)}%</span>
              </div>
            </div>

            {/* Interpretation Note */}
            {liquidityRatio < 20 && (
              <p className="text-[#F87171] font-semibold mb-4">
                Note: Liquidity is low. May face trouble in a sudden emergency.
              </p>
            )}
            {liquidityRatio >= 20 && liquidityRatio < 40 && (
              <p className="text-[#FFD700] font-semibold mb-4">
                Note: Moderate liquidity. Consider increasing liquid investments.
              </p>
            )}
            {liquidityRatio >= 40 && (
              <p className="text-[#34D399] font-semibold mb-4">
                Good liquidity position for emergencies and opportunities.
              </p>
            )}

            {/* Action Points */}
            <div className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
              <p className="text-[#FFD700] font-bold text-sm mb-2">ACTION POINTS:</p>
              <ul className="space-y-1 text-gray-300 text-sm">
                {liquidityRatio < 20 && (
                  <>
                    <li className="flex items-start space-x-2">
                      <span className="text-gray-500 mt-1">•</span>
                      <span>Build emergency fund (Liquid Assets).</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-gray-500 mt-1">•</span>
                      <span>Review illiquid assets (Real Estate/Gold).</span>
                    </li>
                  </>
                )}
                {debtRatio > 30 && (
                  <li className="flex items-start space-x-2">
                    <span className="text-gray-500 mt-1">•</span>
                    <span>Create a debt repayment strategy.</span>
                  </li>
                )}
                <li className="flex items-start space-x-2">
                  <span className="text-gray-500 mt-1">•</span>
                  <span>Review net worth quarterly to track progress.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right Side - Charts */}
        <div className="space-y-6">
          {/* Bar Chart */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D5A8F" />
                <XAxis dataKey="name" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" tickFormatter={(val) => `₹${(val/100000).toFixed(0)}L`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F', borderRadius: '8px', color: '#fff' }}
                  formatter={(value) => [`₹${Math.round(value).toLocaleString('en-IN')}`, '']}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {barChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Net Worth Display */}
          <div className={`rounded-xl p-6 text-center border ${netWorth >= 0 ? 'bg-[#34D399]/10 border-[#34D399]' : 'bg-[#F87171]/10 border-[#F87171]'}`} data-testid="networth-display">
            <p className={`text-xl font-bold ${netWorth >= 0 ? 'text-[#34D399]' : 'text-[#F87171]'}`}>
              Net Worth Snapshot: {formatRupees(netWorth)}
            </p>
          </div>

          {/* Asset Distribution Pie Chart */}
          {assetBreakdownData.length > 0 && (
            <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <h3 className="text-lg font-bold text-[#FFD700] mb-4">Asset Distribution</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={assetBreakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {assetBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F', borderRadius: '8px', color: '#fff' }}
                    formatter={(value) => [`₹${Math.round(value).toLocaleString('en-IN')}`, '']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Arth Gurukul AI Analysis */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#D4AF37]">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-[#FFD700]">✦</span>
            <h3 className="text-xl font-bold text-[#FFD700]">Arth Gurukul AI</h3>
          </div>
        </div>

        {loadingAI ? (
          <div className="flex items-center justify-center py-8">
            <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
            <span className="ml-3 text-gray-400">Analyzing your financial position...</span>
          </div>
        ) : aiAnalysis ? (
          <div className="space-y-4">
            {/* AI Insights */}
            {aiAnalysis.insights?.map((insight, idx) => (
              <div 
                key={idx} 
                className={`rounded-lg p-4 flex items-start space-x-3 ${
                  insight.type === 'warning' ? 'bg-[#F87171]/10 border border-[#F87171]' :
                  insight.type === 'success' ? 'bg-[#34D399]/10 border border-[#34D399]' :
                  'bg-[#60A5FA]/10 border border-[#60A5FA]'
                }`}
              >
                {insight.type === 'warning' ? <FaExclamationTriangle className="text-[#F87171] mt-1" /> :
                 insight.type === 'success' ? <FaCheckCircle className="text-[#34D399] mt-1" /> :
                 <FaInfoCircle className="text-[#60A5FA] mt-1" />}
                <div>
                  <p className={`font-semibold ${
                    insight.type === 'warning' ? 'text-[#F87171]' :
                    insight.type === 'success' ? 'text-[#34D399]' : 'text-[#60A5FA]'
                  }`}>{insight.title}</p>
                  <p className="text-gray-400 text-sm">{insight.message}</p>
                </div>
              </div>
            ))}

            {/* Summary */}
            {aiAnalysis.summary && (
              <div className="bg-[#0A1929] rounded-lg p-4 mt-4 border border-[#2D5A8F]">
                <p className="text-gray-300">{aiAnalysis.summary}</p>
              </div>
            )}

            {/* AI Action Points */}
            {aiAnalysis.action_points?.length > 0 && (
              <div className="mt-4">
                <p className="text-white font-semibold mb-2">AI Recommendations:</p>
                <ul className="space-y-1">
                  {aiAnalysis.action_points.map((point, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-gray-300 text-sm">
                      <span className="w-1.5 h-1.5 bg-[#FFD700] rounded-full"></span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Refresh Button */}
            <button
              onClick={fetchAIAnalysis}
              className="mt-4 px-4 py-2 bg-[#1E3A5F] text-[#FFD700] rounded-lg hover:bg-[#2D5A8F] transition-all text-sm flex items-center space-x-2 border border-[#2D5A8F]"
            >
              <FaBrain />
              <span>Refresh Analysis</span>
            </button>
          </div>
        ) : (
          <button
            onClick={fetchAIAnalysis}
            className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center justify-center space-x-2"
            data-testid="get-ai-analysis-btn"
          >
            <FaBrain />
            <span>Get AI Analysis</span>
          </button>
        )}
      </div>

      {/* Save Button */}
      <button
        onClick={saveNetWorth}
        disabled={saving}
        className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all disabled:opacity-50"
        data-testid="save-networth-btn"
      >
        {saving ? 'Saving...' : 'Save Net Worth Data'}
      </button>
    </div>
  );
};

export default NetWorthSnapshot;
